import React from "react"
import { useLocation } from "@reach/router"
import { normaliseArticlePath } from "../data/article-metadata"
import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core"
import ThemeProvider from "@material-ui/styles/ThemeProvider"
import Layout from "./layout"
import SEO from "./seo"
import ArticleHero from "./article-hero"
import { CalendarDiagram } from "./diagrams/article-diagrams"
import { articleArtwork } from "../data/article-artwork-data"
import theme from "../theme"
import { articles } from "../data/guides"

interface Props {
  quickAnswer?: { question: string; answer: string }
  companion?: { url: string; label: string; note: string }
  title: string
  description: string
  intro: string
  rows: string[][]
  prompt: string
  tableTitle?: string
  promptInstructions?: string
  steps: { title: string; paragraphs: string[] }[]
  cta: string
  relatedUrl: string
  relatedTitle: string
}

const CalendarTutorial = ({
  title,
  quickAnswer,
  companion,
  description,
  intro,
  rows,
  prompt,
  tableTitle = "The week you’ll build",
  promptInstructions = "Replace the bracketed date with the Monday of your chosen week, then paste this into Magic Generate.",
  steps,
  cta,
  relatedUrl,
  relatedTitle,
}: Props) => {
  const { pathname } = useLocation()
  const article = articles.find(item => item.url === normaliseArticlePath(pathname))
  const slug = article?.url.split("/").filter(Boolean).pop() || ""
  const artwork = articleArtwork[slug]
  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <SEO title={title} description={description} />
        <Box
          component="article"
          style={{ maxWidth: 960, margin: "0 auto", padding: "40px 0 56px" }}
        >
          <Button href="/guides/" color="primary">
            Back to all guides
          </Button>
          <ArticleHero
            title={title}
            intro={intro}
            category={article?.group || "Use cases"}
            image={artwork.image}
            imageAlt={artwork.alt}
          />
          <Box style={{ maxWidth: 800, margin: "0 auto" }}>
            {quickAnswer && (
              <section aria-label="Quick answer" style={{ marginBottom: 24 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {quickAnswer.question}
                </Typography>
                <Typography paragraph>{quickAnswer.answer}</Typography>
              </section>
            )}
            {companion && (
              <Typography paragraph>
                <a href={companion.url} download>
                  {companion.label}
                </a>
                . {companion.note}
              </Typography>
            )}
            <CalendarDiagram slug={slug} />
            {rows.length > 0 && (
              <Box
                style={{
                  margin: "32px 0",
                  padding: 20,
                  background: "#f3f7ff",
                  borderRadius: 16,
                }}
              >
                <Typography variant="h5" component="h2" gutterBottom>
                  {tableTitle}
                </Typography>
                <Typography paragraph>
                  This example is a starting point. Use dates and activities
                  that fit your own week.
                </Typography>
                <div style={{ overflowX: "auto" }}>
                  <Table size="small" aria-label="Example weekly calendar">
                    <TableHead>
                      <TableRow>
                        {["Day", "Time", "Activity", "Duration"].map(label => (
                          <TableCell key={label}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.join("-")}>
                          {row.map((cell, index) => (
                            <TableCell
                              key={index}
                              component={index === 0 ? "th" : "td"}
                              scope={index === 0 ? "row" : undefined}
                            >
                              {cell}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Box>
            )}
            {prompt && (
              <>
                <Typography variant="h5" component="h2" gutterBottom>
                  A prompt to try
                </Typography>
                <Typography paragraph>{promptInstructions}</Typography>
                <Box
                  component="pre"
                  style={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    padding: 20,
                    border: "1px solid #d9e5f8",
                    borderRadius: 12,
                    fontSize: 15,
                  }}
                >
                  {prompt}
                </Box>
              </>
            )}
            {steps.map(step => (
              <Box
                component="section"
                key={step.title}
                style={{ marginTop: 32 }}
              >
                <Typography variant="h5" component="h2" gutterBottom>
                  {step.title}
                </Typography>
                {step.paragraphs.map(paragraph => (
                  <Typography paragraph key={paragraph}>
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}
            <Box
              style={{
                marginTop: 40,
                padding: 24,
                background: "#f3f7ff",
                borderRadius: 16,
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                Make the next week easier to see
              </Typography>
              <Typography paragraph>
                Start with a few activities, check the details, and build from
                there.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href="https://app.schoolcalendar.me"
              >
                {cta}
              </Button>
            </Box>
            <Typography style={{ marginTop: 28 }}>
              Next guide: <a href={relatedUrl}>{relatedTitle}</a>
            </Typography>
          </Box>
        </Box>
      </Layout>
    </ThemeProvider>
  )
}

export default CalendarTutorial
